// src/userFlow/UserProfilePage.js
import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Shield, Trash2, ArrowLeft, Loader2 } from 'lucide-react';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';

export default function UserProfilePage() {
  const navigate = useNavigate();

  // banners / status
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // profile form
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    shipping_address1: '',
    shipping_address2: '',
    shipping_city: '',
    shipping_region: '',
    shipping_postal_code: '',
    shipping_country: '',
  });

  // password form
  const [pwdForm, setPwdForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  // --- Styles (one source of truth) ----------------------------------------
  const INPUT = useMemo(
    () =>
      "mt-1 w-full border border-gray-300 px-3 py-2 rounded-lg bg-white shadow-sm " +
      "placeholder:text-gray-400 " +
      "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
    []
  );
  const LABEL = "block text-sm font-medium text-gray-700";
  const CARD = "bg-white rounded-xl shadow p-6";
  // -------------------------------------------------------------------------

  // Fetch my profile on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/users/me`, { credentials: 'include' });
        if (res.status === 401) {
          navigate('/signin');
          return;
        }
        const data = await res.json();
        if (!alive) return;
        setForm(f => ({
          ...f,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          shipping_address1: data.shipping_address1 || '',
          shipping_address2: data.shipping_address2 || '',
          shipping_city: data.shipping_city || '',
          shipping_region: data.shipping_region || '',
          shipping_postal_code: data.shipping_postal_code || '',
          shipping_country: data.shipping_country || '',
        }));
      } catch {
        setErr('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }
  function onPwdChange(e) {
    const { name, value } = e.target;
    setPwdForm(prev => ({ ...prev, [name]: value }));
  }

  function validateProfile() {
    if (!form.first_name.trim()) return 'First name is required';
    if (!form.last_name.trim()) return 'Last name is required';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter a valid email';
    return null;
  }

  async function saveProfile() {
    const v = validateProfile();
    if (v) { setErr(v); setMsg(null); return; }
    setErr(null); setMsg(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile: form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update profile');
      setMsg('Profile updated successfully.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    if (!pwdForm.old_password || !pwdForm.new_password || !pwdForm.confirm_password) {
      setErr('Please fill in all password fields.'); setMsg(null);
      return;
    }
    if (pwdForm.new_password.length < 8) {
      setErr('New password must be at least 8 characters.'); setMsg(null);
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setErr('New password and confirmation do not match.'); setMsg(null);
      return;
    }

    setErr(null); setMsg(null);
    setPwdSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: {
            old_password: pwdForm.old_password,
            new_password: pwdForm.new_password,
          }
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      setMsg('Password changed successfully.');
      setPwdForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      setErr(e.message);
    } finally {
      setPwdSaving(false);
    }
  }

  async function deleteAccount() {
    setDeleting(true);
    setErr(null); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/users/me`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete account');
      }
      navigate('/signin');
      window.location.reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" data-qa="profile-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40" data-qa="profile-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 rounded-lg p-2 hover:bg-gray-100"
            aria-label="Back"
            data-qa="profile-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-gray-800" data-qa="profile-title">My Profile</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-qa="profile-main">
        {/* Always-mounted banners */}
        <QaAlert id="profile-success" dataQa="profile-alert-success" message={msg} variant="success" />
        <QaAlert id="profile-error" dataQa="profile-alert-error" message={err} variant="error" />

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500" data-qa="profile-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading profile…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-qa="profile-grid">
            {/* Profile card */}
            <section className={`lg:col-span-2 ${CARD}`} data-qa="profile-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-orange-500" />
                  <h2 className="text-lg font-semibold" data-qa="profile-card-title">Account Details</h2>
                </div>
                <span className="text-xs text-gray-500">Fields are editable</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-qa="profile-form">
                <div>
                  <label className={LABEL} htmlFor="first_name">First Name</label>
                  <input
                    id="first_name"
                    name="first_name"
                    value={form.first_name}
                    onChange={onChange}
                    placeholder="e.g., Jane"
                    autoComplete="given-name"
                    className={INPUT}
                    data-qa="profile-first-name-input"
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="last_name">Last Name</label>
                  <input
                    id="last_name"
                    name="last_name"
                    value={form.last_name}
                    onChange={onChange}
                    placeholder="e.g., Doe"
                    autoComplete="family-name"
                    className={INPUT}
                    data-qa="profile-last-name-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className={INPUT}
                    data-qa="profile-email-input"
                  />
                </div>

                <div className="md:col-span-2 pt-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2" data-qa="profile-shipping-title">
                    Shipping Address
                  </h3>
                </div>

                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="shipping_address1">Address Line 1</label>
                  <input
                    id="shipping_address1"
                    name="shipping_address1"
                    value={form.shipping_address1}
                    onChange={onChange}
                    placeholder="123 Main St"
                    autoComplete="address-line1"
                    className={INPUT}
                    data-qa="profile-address1-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="shipping_address2">Address Line 2</label>
                  <input
                    id="shipping_address2"
                    name="shipping_address2"
                    value={form.shipping_address2}
                    onChange={onChange}
                    placeholder="Apt, suite, unit, etc. (optional)"
                    autoComplete="address-line2"
                    className={INPUT}
                    data-qa="profile-address2-input"
                  />
                </div>

                <div>
                  <label className={LABEL} htmlFor="shipping_city">City</label>
                  <input
                    id="shipping_city"
                    name="shipping_city"
                    value={form.shipping_city}
                    onChange={onChange}
                    placeholder="City"
                    autoComplete="address-level2"
                    className={INPUT}
                    data-qa="profile-city-input"
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="shipping_region">Region</label>
                  <input
                    id="shipping_region"
                    name="shipping_region"
                    value={form.shipping_region}
                    onChange={onChange}
                    placeholder="State / Province"
                    autoComplete="address-level1"
                    className={INPUT}
                    data-qa="profile-region-input"
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="shipping_postal_code">Postal Code</label>
                  <input
                    id="shipping_postal_code"
                    name="shipping_postal_code"
                    value={form.shipping_postal_code}
                    onChange={onChange}
                    placeholder="Postal / ZIP"
                    autoComplete="postal-code"
                    className={INPUT}
                    data-qa="profile-postal-input"
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="shipping_country">Country</label>
                  <input
                    id="shipping_country"
                    name="shipping_country"
                    value={form.shipping_country}
                    onChange={onChange}
                    placeholder="Country"
                    autoComplete="country-name"
                    className={INPUT}
                    data-qa="profile-country-input"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:opacity-60"
                  data-qa="profile-save-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </button>

                <Link
                  to="/orders"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-qa="profile-orders-link"
                >
                  View Order History
                </Link>
              </div>
            </section>

            {/* Password & Danger Zone */}
            <section className="space-y-6" data-qa="profile-side">
              <div className={CARD} data-qa="profile-password-card">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <h2 className="text-lg font-semibold" data-qa="profile-password-title">Change Password</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={LABEL} htmlFor="old_password">Current Password</label>
                    <input
                      id="old_password"
                      type="password"
                      name="old_password"
                      value={pwdForm.old_password}
                      onChange={onPwdChange}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className={INPUT}
                      data-qa="profile-pwd-current-input"
                    />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="new_password">New Password</label>
                    <input
                      id="new_password"
                      type="password"
                      name="new_password"
                      value={pwdForm.new_password}
                      onChange={onPwdChange}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      className={INPUT}
                      data-qa="profile-pwd-new-input"
                    />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="confirm_password">Confirm New Password</label>
                    <input
                      id="confirm_password"
                      type="password"
                      name="confirm_password"
                      value={pwdForm.confirm_password}
                      onChange={onPwdChange}
                      autoComplete="new-password"
                      placeholder="Re-enter new password"
                      className={INPUT}
                      data-qa="profile-pwd-confirm-input"
                    />
                  </div>

                  <button
                    onClick={changePassword}
                    disabled={pwdSaving}
                    className="mt-1 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white font-semibold disabled:opacity-60"
                    data-qa="profile-password-update-btn"
                  >
                    {pwdSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Update Password
                  </button>
                </div>
              </div>

              <div className={CARD} data-qa="profile-danger-card">
                <div className="flex items-center gap-2 mb-3">
                  <Trash2 className="w-5 h-5 text-red-500" />
                  <h2 className="text-lg font-semibold">Danger Zone</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Deleting your account is permanent. Your session will end immediately.
                </p>
                <button
                  onClick={() => setShowDelete(true)}
                  className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                  data-qa="profile-delete-open-btn"
                >
                  Delete My Account
                </button>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Delete confirmation modal */}
      {showDelete && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-qa="profile-delete-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete account?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This action cannot be undone. Are you sure you want to permanently delete your account?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDelete(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                data-qa="profile-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={deleteAccount}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                data-qa="profile-delete-confirm-btn"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
